import type { CalendarDto, UpdateCalendarBodyDto } from '../core/calendars';
import { IDataOptions } from '../core/common';
import type { HttpClient } from './http';
import { catch404ReturnNull, createQueryParams } from './util';

export interface CalendarsService {
  synchronizeCalendars: () => Promise<void>;
  getCalendars: () => Promise<CalendarDto[]>;
  getCalendarById: (
    id: string,
    params: IDataOptions,
  ) => Promise<CalendarDto | null>;
  bulkUpdateCalendars: (
    params: UpdateCalendarBodyDto[],
  ) => Promise<CalendarDto[]>;
  updateCalendar: (data: UpdateCalendarBodyDto) => Promise<CalendarDto[]>;
  syncCalendars: () => Promise<void>;
}

export function createCalendarsService(
  httpClient: HttpClient,
  baseUrl: string,
): CalendarsService {
  const BASE_URL = `${baseUrl}/calendars`;

  return {
    async synchronizeCalendars(): Promise<void> {
      return httpClient<void>(`${BASE_URL}/syncronize-calendars`, 'post');
    },

    async getCalendars(): Promise<CalendarDto[]> {
      return httpClient<CalendarDto[]>(`${BASE_URL}`, 'get');
    },

    getCalendarById: (
      id: string,
      params: IDataOptions,
    ): Promise<CalendarDto | null> => {
      const queryParams = createQueryParams(params as Record<string, unknown>);
      return catch404ReturnNull(() =>
        httpClient<CalendarDto>(`${BASE_URL}/${id}${queryParams}`, 'get'),
      );
    },

    async bulkUpdateCalendars(
      params: UpdateCalendarBodyDto[],
    ): Promise<CalendarDto[]> {
      return httpClient<CalendarDto[]>(`${BASE_URL}/bulk`, 'patch', {
        body: { data: params },
      });
    },

    updateCalendar: async (
      data: UpdateCalendarBodyDto,
    ): Promise<CalendarDto[]> => {
      return await httpClient<CalendarDto[]>(`${BASE_URL}/bulk`, 'patch', {
        body: { data: [data] },
      });
    },

    syncCalendars: async (): Promise<void> => {
      return httpClient(`${BASE_URL}/sync`, 'post');
    },
  };
}
